import { TypedDocumentNode, gql, useSuspenseQuery } from '@apollo/client';
import classNames from 'classnames';
import { Suspense, useMemo, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTimeoutFn } from 'react-use';
import styled, { keyframes } from 'styled-components';

import { Link, useTypedParams } from '@sorare/routing';

import {
  Position,
  ScoreStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { EmojiFlood } from '@sorare/core/src/atoms/animations/EmojiFlood';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { Stadium } from '@sorare/core/src/components/animations/Stadium';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { PositionIcon } from '@sorare/core/src/components/lineup/PositionIcon';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { FOOTBALL } from '@sorare/core/src/constants/__generated__/routes';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useTransitionApi } from '@sorare/core/src/hooks/ui/useTransitionApi';
import { range } from '@sorare/core/src/lib/arrays';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { regularMaterial, thinMaterial } from '@sorare/core/src/style/utils';

import { positionShortNames } from '@sorare/gameplay/src/lib/so5';

import laligaEASports from 'assets/minigames/laligaEASports.png';
import laligaSorare from 'assets/minigames/laligaSorare.png';
import {
  BUNDESLIGA_PLAYERS,
  LALIGA_PLAYERS,
  PL_PLAYERS,
} from 'pages/minigames/pve_/PLAYERS';

import { PVEQuery, PVEQueryVariables } from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

type Card = PVEQuery['anyCards'][number];

const Root = styled(Vertical)`
  position: relative;
  z-index: 1;
  justify-content: space-between;
  height: var(--100vh);
  overflow: hidden;
`;

const StadiumWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  bottom: -25vmax;
  pointer-events: none;
`;

const appearIn = keyframes`
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  50% {
    scale: 1.1;
  }
  100% {
    scale: 1;
  }
`;
const Slots = styled(Vertical)`
  border-radius: var(--unit);
  background: var(--c-nd-100);
  ${thinMaterial}
  gap: var(--half-unit);
  padding: 1.5vmin;
`;
const Slot = styled(Vertical).attrs({ center: true })`
  flex: 1;
  gap: 1;
  justify-content: stretch;
`;

const CardImgWrapper = styled.div`
  width: 100%;
  aspect-ratio: var(--card-aspect-ratio);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-100);
  > *:first-child {
    animation: 0.3s ease ${appearIn} forwards;
  }
  overflow: hidden;
`;
const pulse = keyframes`
    0% {
        transform: scale(0.98);
    }

    50% {
        transform: scale(1.02);
    }

    100% {
        transform: scale(0.98);
    }
`;

const pulsate = keyframes`
    0% {
        transform: scale(1);
        opacity: 1;
    }

    50% {
        transform: scale(1.2);
        opacity: 0;
    }

    100% {
        transform: scale(1);
        opacity: 0;
    }
`;

const Pulsation = styled.span`
  inset: 0;
  border: 2px solid white;
  border-radius: var(--unit);
  position: absolute;
  animation: ${pulsate} 2s infinite;
  aspect-ratio: var(--card-aspect-ratio);
  opacity: 0;
`;

const Pick = styled.div`
  animation: 0.3s ease ${appearIn} forwards;
  animation: ${pulse} 1s 4s infinite;
`;

const CenterContent = styled(Horizontal)`
  padding: 0 var(--double-unit);
  margin: 5% 0;
  width: 100%;
  column-gap: 25%;
  justify-content: space-around;
`;

const Content = styled(Vertical)`
  width: 500px;
  max-width: 100%;
  margin: auto;
  justify-content: space-around;
  flex: 1;
`;

const Heading = styled(Vertical)`
  bottom: 100%;
  text-align: center;
  width: 100%;
`;

const StyledHeading = styled.h2`
  font-size: 3.2vmin;
`;

const StyledBody = styled.p`
  font-size: 1.8vmin;
  color: var(--c-nd-600);
`;

const ResultRoot = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  flex: 1;
  z-index: 1;
  justify-content: space-around;
  transform-origin: center;
  opacity: 0;
  pointer-events: none;
  &.showResult {
    pointer-events: auto;
    animation: 0.3s ease ${fadeIn} forwards;
  }
`;
const ResultTitle = styled(Vertical)`
  padding: var(--double-unit);
  visibility: hidden;
  border-radius: var(--unit);
  overflow: hidden;
  opacity: 0;
  ${regularMaterial}
  &.displayResult {
    visibility: visible;
    animation: 1s ease ${fadeIn} forwards;
  }
`;
const Title = styled(LabelL)`
  transform-origin: left top;
  width: 100%;
`;

const ScoreTitle = styled(Horizontal).attrs({ gap: 0, center: true })`
  font-size: 60px;
  line-height: 1;
  text-transform: uppercase;
  font-family: var(--rigid-square);
  z-index: 1;
  @media ${tabletAndAbove} {
    font-size: 80px;
  }
`;

const SlotPlaceholder = styled(Vertical)`
  height: 100%;
  opacity: 0.1;
  word-break: break-word;
  padding: var(--half-unit);
  &.selected {
    opacity: 1;
    background: rgba(var(--c-rgb-white), 0.05);
  }
`;

const PickButton = styled(ButtonBase)`
  position: relative;
  max-width: 30vw;
  flex: 1;
`;

const StyledA = styled.button`
  color: var(--c-brand-300);
  text-decoration: underline;
`;

const StyledLink = styled(Link)`
  color: var(--c-brand-300);
  text-decoration: underline;
`;

const LogoContainer = styled(Horizontal).attrs({ center: true })`
  padding-bottom: var(--double-unit);
`;

const LaligaLogo = styled.img`
  width: clamp(12px, 6rem, 120px);
  height: auto;
`;

const competitions = {
  PremierLeague: {
    fieldUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-england/stadium-in_season.jpg',
  },
  LALIGA: {
    fieldUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-spain/stadium-in_season.jpg',
  },
  Bundesliga: {
    fieldUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-germany/stadium-in_season.jpg',
  },
};

const messages = defineMessages({
  yourTeam: {
    id: 'football.minigames.pve_.yourTeam',
    defaultMessage: 'Your Team',
  },
  opponentTeam: {
    id: 'football.minigames.pve_.opponentTeam',
    defaultMessage: 'Your opponent',
  },
});

const randomInt = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

const randomIntExcept = (min: number, max: number, excepts: number[]) => {
  let result;
  do {
    result = Math.floor(Math.random() * (max - min + 1) + min);
  } while (excepts.includes(result));
  return result;
};

const DelayedScoring = ({
  score: scoreProp,
  index,
}: {
  score: number;
  index: number;
}) => {
  const [score, setScore] = useState(0);
  useTimeoutFn(() => setScore(scoreProp), index * 600);
  return (
    <div style={{ opacity: score, transition: '.3s ease opacity' }}>
      <Score
        score={score}
        animationDelayMs={0}
        scoreStatus={ScoreStatus.FINAL}
        scoreType="PLAYER_SCORE"
      />
    </div>
  );
};
const Result = ({
  attempt,
  yourTeam,
  opponentTeam,
  showResult,
  retry,
  leagueTitle,
}: {
  attempt: number;
  yourTeam: Card[];
  opponentTeam: Card[];
  showResult: boolean;
  leagueTitle: string;
  retry: (() => void) | null;
}) => {
  const [displayResult, setDisplayResult] = useState(false);
  const navigate = useNavigate();
  const { signUp } = useConnectionContext();
  const { currentUser } = useCurrentUserContext();

  useTimeoutFn(() => {
    setDisplayResult(true);
  }, 3000);
  const yourScore = yourTeam.reduce((acc, cur) => (cur.score || 0) + acc, 0);
  const opponentScore = opponentTeam.reduce(
    (acc, cur) => (cur.score || 0) + acc,
    0
  );
  const isFirstAttempt = attempt === 0;
  const looseTitleLaLiga = (
    <Vertical>
      <div>
        <FormattedMessage
          id="football.minigames.pve_.looseTitle.LaLiga.firstAttempt.title"
          defaultMessage="Ouch… You lost!"
        />
      </div>
      <div>
        <FormattedMessage
          id="football.minigames.pve_.looseTitle.firstAttempt.subtitle"
          defaultMessage="Think wisely, scoring is impacted by key actions in the last 5 games: goals, assists, decisive tackles, etc."
        />
      </div>
    </Vertical>
  );

  const looseTitle = isFirstAttempt ? (
    <FormattedMessage
      id="football.minigames.looseTitle.firstAttempt.title"
      defaultMessage="Ouch… It's {leagueTitle} here, not your Sunday league!"
      values={{ leagueTitle }}
    />
  ) : (
    <FormattedMessage
      id="football.minigames.looseTitle.SecondOrMoreAttempt.title"
      defaultMessage="Another loss… looks like {leagueTitle} is really not for you!"
      values={{ leagueTitle }}
    />
  );

  const titleToDisplay =
    leagueTitle === 'LALIGA' && isFirstAttempt ? looseTitleLaLiga : looseTitle;

  let gameResult;
  if (yourScore > opponentScore) {
    gameResult = (
      <FormattedMessage
        id="football.minigames.pve_.result.won.title"
        defaultMessage="Wow good job! You are ready to coach in {leagueTitle}. Start now
    on {link}"
        values={{
          leagueTitle,
          link: currentUser ? (
            <StyledLink to={FOOTBALL}>sorare.com </StyledLink>
          ) : (
            <StyledA onClick={signUp}>sorare.com</StyledA>
          ),
        }}
      />
    );
  } else if (yourScore === opponentScore) {
    gameResult = (
      <FormattedMessage
        id="football.minigames.pve_.result.draw.title"
        defaultMessage="That's a draw! Try again!"
      />
    );
  } else {
    gameResult = titleToDisplay;
  }

  const handleNavigate = () => (currentUser ? navigate(FOOTBALL) : signUp());

  return (
    <ResultRoot className={classNames({ showResult })}>
      <ScoreTitle>
        <AnimatedNumber
          value={opponentScore}
          animationProps={{ config: { duration: 3000 } }}
        />
      </ScoreTitle>
      <ResultTitle center gap={2} className={classNames({ displayResult })}>
        <Title
          bold
          className={classNames({
            'text-center': !isFirstAttempt || gameResult !== undefined,
          })}
        >
          {gameResult}
        </Title>
        <Horizontal>
          {retry && (
            <Button color="tertiary" size="small" onClick={retry}>
              <FormattedMessage
                id="football.minigames.pve_.button.tryAgain"
                defaultMessage="Try again"
              />
            </Button>
          )}
          <Button
            color="secondary"
            size="small"
            onClick={() => {
              if ('ReactNativeWebView' in window) {
                (
                  window.ReactNativeWebView as {
                    postMessage: (data: string) => void;
                  }
                ).postMessage('displayOverlay');
              } else {
                handleNavigate();
              }
            }}
          >
            {retry ? (
              <FormattedMessage
                id="football.minigames.pve_.button.startCoaching"
                defaultMessage="Start coaching"
              />
            ) : (
              <FormattedMessage
                id="football.minigames.pve_.button.keepPlaying"
                defaultMessage="Keep playing"
              />
            )}
          </Button>
        </Horizontal>
      </ResultTitle>
      <ScoreTitle>
        <AnimatedNumber
          value={yourScore}
          animationProps={{ config: { duration: 3000 } }}
        />
      </ScoreTitle>
      <div style={{ pointerEvents: 'none' }}>
        {displayResult && (
          <EmojiFlood emoji={yourScore > opponentScore ? '🔥' : '🤣'} />
        )}
      </div>
    </ResultRoot>
  );
};

const POSITION = ['GK', 'DEF', 'MD', 'FW', 'EX'] as const;

const LEAGUES_DATA_MAP = {
  epl: {
    data: PL_PLAYERS,
    title: 'Premier League',
  },
  laliga: {
    data: LALIGA_PLAYERS,
    title: 'LALIGA',
  },
  bundesliga: {
    data: BUNDESLIGA_PLAYERS,
    title: 'Bundesliga',
  },
};

const useData = () => {
  const pickedPlayers: number[] = [];
  const data: any[][] = [];
  const { league } = useTypedParams<{
    league: keyof typeof LEAGUES_DATA_MAP;
  }>();
  const playersData = LEAGUES_DATA_MAP[league]?.data;

  if (!playersData) {
    return [];
  }
  const allPlayers = [
    ...playersData.GK,
    ...playersData.DEF,
    ...playersData.MD,
    ...playersData.FW,
  ] as const;

  POSITION.forEach((pos, i) => {
    if (pos !== 'EX') {
      const playersLength = playersData[pos].length;
      const firstPlayerIndex = randomInt(0, playersLength - 1);
      const secondPlayerIndex = randomIntExcept(0, playersLength - 1, [
        firstPlayerIndex,
      ]);
      const positionsBefore = POSITION.slice(0, i);
      const beginningIndex = positionsBefore.reduce(
        // @ts-expect-error EX is not a possible value here
        (acc, cur) => acc + playersData[cur].length,
        0
      );
      const finalFirstPlayerIndex = beginningIndex + firstPlayerIndex;
      const finalSecondPlayerIndex = beginningIndex + secondPlayerIndex;
      data.push([
        allPlayers[finalFirstPlayerIndex],
        allPlayers[finalSecondPlayerIndex],
      ]);
      pickedPlayers.push(finalFirstPlayerIndex, finalSecondPlayerIndex);
    } else {
      const firstPlayerIndex = randomIntExcept(
        playersData.GK.length,
        allPlayers.length - 1,
        pickedPlayers
      );
      const secondPlayerIndex = randomIntExcept(
        playersData.GK.length,
        allPlayers.length - 1,
        [firstPlayerIndex, ...pickedPlayers]
      );
      data.push([allPlayers[firstPlayerIndex], allPlayers[secondPlayerIndex]]);
    }
  });
  return data;
};

type TeamProp = {
  team: Card[];
  displayResult: boolean;
  label: string;
  currentSelection: number;
  viewTransition?: boolean;
};

const POSITIONS_MAP = [
  {
    position: Position.Goalkeeper,
  },
  {
    position: Position.Defender,
  },
  {
    position: Position.Midfielder,
  },
  {
    position: Position.Forward,
  },
  {
    position: Position.Unknown,
    label: positionShortNames['Extra Player'],
  },
];

const PVE_QUERY = gql`
  query PVEQuery($slugs: [String!]!) {
    anyCards(slugs: $slugs) {
      slug
      pictureUrl
      score: averageScore(type: LAST_FIVE_SO5_AVERAGE_SCORE)
    }
  }
` as TypedDocumentNode<PVEQuery, PVEQueryVariables>;
const Team = ({
  team,
  displayResult,
  label,
  currentSelection,
  viewTransition = false,
}: TeamProp) => {
  return (
    <Slots>
      <LabelS color="var(--c-nd-700)" bold>
        {label}
      </LabelS>
      <Horizontal>
        {range(5).map(i => {
          const position = POSITIONS_MAP[i];
          return (
            <Slot key={i} className="bottom">
              <CardImgWrapper>
                {team.at(i) ? (
                  <CardImg
                    src={team[i].pictureUrl}
                    style={{
                      viewTransitionName: viewTransition
                        ? `card-${team[i].slug}`
                        : undefined,
                    }}
                    width={320}
                  />
                ) : (
                  <SlotPlaceholder
                    center
                    className={classNames({ selected: currentSelection === i })}
                  >
                    <PositionIcon position={POSITIONS_MAP[i].position} />
                    <LabelS bold>
                      <FormattedMessage
                        {...(position.label
                          ? position.label
                          : positionShortNames[position.position])}
                      />
                    </LabelS>
                  </SlotPlaceholder>
                )}
              </CardImgWrapper>
              {team.at(i) ? (
                <>
                  {displayResult ? (
                    <DelayedScoring score={team[i].score || 0} index={i} />
                  ) : (
                    <Score score={0} scoreStatus={ScoreStatus.PENDING} />
                  )}
                </>
              ) : (
                <div style={{ opacity: 0 }}>
                  <Score score={0} scoreStatus={ScoreStatus.PENDING} />
                </div>
              )}
            </Slot>
          );
        })}
      </Horizontal>
    </Slots>
  );
};

const PvEPageComponent = ({
  retry,
  cardSlugs,
  attempt,
  leagueTitle,
}: {
  retry: (() => void) | null;
  cardSlugs: string[][];
  attempt: number;
  leagueTitle: string;
}) => {
  const { formatMessage } = useIntl();
  const { updateDOM } = useTransitionApi();
  const isAIPick = false;
  const [state, setState] = useState<
    Record<'yourTeam' | 'opponentTeam', Card[]>
  >({
    yourTeam: [],
    opponentTeam: [],
  });
  const [currentSelection, setCurrentSelection] = useState(0);
  const displayResult = state.yourTeam.length === 5;
  const { data } = useSuspenseQuery(PVE_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      slugs: cardSlugs.flat(),
    },
  });

  const playersData = cardSlugs.map(item =>
    item.map(
      cardSlug =>
        data.anyCards.find(
          card => card.slug === cardSlug && card.score && card.score > 0
        )!
    )
  );

  const selectionData = playersData[currentSelection];

  const normalizedLeagueTitle = leagueTitle.replace(
    /\s+/g,
    ''
  ) as keyof typeof competitions;
  const fieldUrl = competitions[normalizedLeagueTitle]?.fieldUrl;
  return (
    <Root>
      <Content gap={0}>
        <Team
          team={state.opponentTeam}
          displayResult={displayResult}
          label={formatMessage(messages.opponentTeam)}
          currentSelection={currentSelection}
        />
        {displayResult ? (
          <Result
            showResult={displayResult}
            yourTeam={state.yourTeam}
            opponentTeam={state.opponentTeam}
            retry={retry}
            attempt={attempt}
            leagueTitle={leagueTitle}
          />
        ) : (
          <>
            <Heading gap={0}>
              <StyledHeading>
                <FormattedMessage
                  id="football.minigames.pve_.Picker.title"
                  defaultMessage="Think you know football?"
                />
                <br />
                <FormattedMessage
                  id="football.minigames.pve_.Picker.subtitle"
                  defaultMessage="Pick the most in-form player!"
                />
              </StyledHeading>
              <StyledBody>
                <FormattedMessage
                  id="football.minigames.pve_.Picker.description"
                  defaultMessage="Scoring is based on performance in the last 5 games"
                />
              </StyledBody>
            </Heading>
            <CenterContent>
              <PickButton
                onClick={() => {
                  updateDOM(() => {
                    setState(prevState => ({
                      yourTeam: prevState.yourTeam.concat(selectionData[0]),
                      opponentTeam: prevState.opponentTeam.concat(
                        selectionData[isAIPick ? randomInt(0, 1) : 1]
                      ),
                    }));

                    setCurrentSelection(prevState => prevState + 1);
                  });
                }}
              >
                <Pick key={currentSelection}>
                  <CardImg
                    src={selectionData[0].pictureUrl}
                    style={{
                      viewTransitionName: `card-${selectionData[0].slug}`,
                    }}
                    width={320}
                  />
                </Pick>
                {currentSelection === 0 && <Pulsation />}
              </PickButton>
              <PickButton
                onClick={() => {
                  updateDOM(() => {
                    setState(prevState => ({
                      yourTeam: prevState.yourTeam.concat(selectionData[1]),
                      opponentTeam: prevState.opponentTeam.concat(
                        selectionData[isAIPick ? randomInt(0, 1) : 0]
                      ),
                    }));
                    setCurrentSelection(prevState => prevState + 1);
                  });
                }}
              >
                <Pick key={currentSelection}>
                  <CardImg
                    src={selectionData[1].pictureUrl}
                    style={{
                      viewTransitionName: `card-${selectionData[1].slug}`,
                    }}
                    width={320}
                  />
                </Pick>
                {currentSelection === 0 && <Pulsation />}
              </PickButton>
            </CenterContent>
          </>
        )}
        <Team
          viewTransition
          label={formatMessage(messages.yourTeam)}
          team={state.yourTeam}
          displayResult={displayResult}
          currentSelection={currentSelection}
        />
        {leagueTitle === 'LALIGA' && (
          <LogoContainer center>
            <LaligaLogo src={laligaSorare} alt="" />
            <LaligaLogo src={laligaEASports} alt="" />
          </LogoContainer>
        )}
      </Content>
      <StadiumWrapper>
        <Stadium
          paths={[
            'M737 717C665 714 619 758 618 790C554 790 381 790 299 790L414 460H737',
            'M737 717C795 718 845 747 851 790L1171 790L1057 460H737',
            'M737 875C790 871 851 849 851 790C923 790 1094 790 1171 790L1452 1595H825',
            'M737 875C650 872 614 834 618 790C577.2 790 388.167 790 299 790L10 1595H638',
          ]}
          fieldUrl={fieldUrl}
          completion={Math.max(0.2 * state.yourTeam.length, 0.19)}
        />
      </StadiumWrapper>
    </Root>
  );
};

const PvEPage = () => {
  const [attempt, setAttempt] = useState(0);
  const { updateDOM } = useTransitionApi();
  const { league } = useTypedParams<PageParams>();

  const cardSlugs = useData();

  // regenerage random card slugs on retry
  // league is also required to trigger data regen on unknown league redirection (there might be a better way to do this)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedCardSlugs = useMemo(() => cardSlugs, [attempt, league]);
  if (
    !Object.keys(LEAGUES_DATA_MAP).includes(league as string) ||
    !cardSlugs.length
  ) {
    return <Navigate replace to="/football/minigames/pve/epl" />;
  }

  return (
    <Suspense fallback={<LoadingIndicator fullScreen />}>
      <PvEPageComponent
        key={attempt}
        attempt={attempt}
        cardSlugs={memoizedCardSlugs}
        leagueTitle={
          LEAGUES_DATA_MAP[league as 'laliga' | 'epl' | 'bundesliga'].title
        }
        retry={
          attempt <= 3
            ? () => {
                updateDOM(() => {
                  setAttempt(prevState => prevState + 1);
                });
              }
            : null
        }
      />
    </Suspense>
  );
};

export default PvEPage;
